import { Component, ElementRef, HostListener, Input, OnInit, Output, ViewChild, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Status } from '@app/interfaces';
import ListingContactViewModel2 from '@app/models/listing-contact-view-model2';
import TrackedEvent from '@app/models/tracked-event';
import ValidationResultModel from '@app/models/validation-result-model';

import {
  ErrorHelperService,
  ExitIntentService,
  ListingContactService,
  LogService,
  NotificationService,
  RouteHelperService,
  WorkflowService,
  DateService,
} from '@app/services';
import { FormsModule } from '@angular/forms';


/**
 *  Component workflow step that confirms a direct mail code
 * <landowner-onboard-code-entry>
 */
@Component({
    selector: "landowner-onboard-code-entry",
    templateUrl: "./landowner-onboard-code-entry.component.html",
    styleUrls: ["./landowner-onboard-code-entry.component.less"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule]
})
export class LandownerOnboardCodeEntryComponent implements OnInit {
  
  private readonly _componentName = 'LandownerOnboardCodeEntryComponent';

  @Input()
  public workflowName = 'Landowner Direct Mail Onboard Workflow';

  @Input()
  public workflowStepName = 'code-confirm';

  @Input()
  public showNoCodeButton = true;

  @Input()
  public showYOA = true;

  public code: string;
  public codeFirst4Chars: string = '';
  public codeLast4Chars: string = '';
  public contact: ListingContactViewModel2;
  public modelPatchStatus: Record<string, Status> = {};
  private dateHelper: DateService
  public validating: boolean = false;
  public validationResult: ValidationResultModel;

  /**
   * The navigation route for when the user presses the "GO" button.  Only used when no workflow is set.
   */
  @Input()
  public proceedRoute: string = '../profit/offer';

  @Output()
  public submitted = new EventEmitter<any>();

  @ViewChild("inputCodeLast4Chars", { static: true }) inputCodeLast4Chars: ElementRef;

  public get errorMessage(): string {
    if (this.validationResult && this.validationResult.issues && this.validationResult.issues.length === 1) {
      return this.validationResult.issues[0];
    } else {
      return undefined;
    }
  }

  public get propertyId(): string {
    return `${this.codeFirst4Chars}${this.codeLast4Chars}`;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private routeHelper: RouteHelperService,
    private exitIntentService: ExitIntentService,
    private listingContactService: ListingContactService,
    private errorHelper: ErrorHelperService,
    private workflowService: WorkflowService,
    private log: LogService,
    private notify: NotificationService
  ) {
    this.exitIntentService.disabled = true;

    if (this.routeHelper.getQueryStringValue('rdc')) {
      this.code = String(this.routeHelper.getQueryStringValue('rdc'));

      if (this.code.length === 8) {
        this.codeFirst4Chars = this.code.substring(0, 4);
        this.codeLast4Chars = this.code.substring(4, 8);
      }
      this.validateCode();
    }
  }

  ngOnInit() {}

  public reset() {
    this.codeFirst4Chars = '';
    this.codeLast4Chars = '';
    this.validationResult = null;
  }

  public onPaste(evt: ClipboardEvent) {
    if (evt && evt.clipboardData && evt.clipboardData.getData('text')) {
      const text = evt.clipboardData.getData('text');

      if (text && text.length === 8) {
        this.codeLast4Chars = text.substring(4, 8);
        this.validateCode();
      }
    }
  }

  private validateCode() {
    this.validating = true;
    const x = this.propertyId.toLocaleUpperCase();
    if (x.length === 0) {
      this.validationResult = null;
      this.validating = false;
      return;
    }
    if (x.length !== 8) {
      this.validationResult = { isValid: false, issues: ['Incomplete reference code'] };
      this.validating = false;
    } else {
      this.listingContactService.validateDirectMailReferenceCode(x).subscribe(
        (result) => {
          console.log(`${this._componentName} subscription result=`, result);
          this.validationResult = result;
          this.validating = false;
      },
        error => {
          console.error(error);
          let msg = this.errorHelper.getErrorMessage(error) || 'A subscription error occured';
          this.validationResult = { isValid: false, issues: [msg] };
          this.validating = false;
        }
      )
    }
  }
  
 
  public onCodeChange(id: string, text: string) {
    // Check to see if we need to focus on the last-4 input
    if (id === 'input-codeFirst4Chars') {
      if (text && text.length === 4) {
        this.inputCodeLast4Chars.nativeElement.focus();
      }
    }
    this.validateCode();
  }

  public onProceed() {
    if (this.workflowStepName) {
      this.workflowService.setStepCompleted(this.workflowName, this.workflowStepName, true);
      let workflowEventData = {} as any;
      if (this.validationResult && this.validationResult.isValid) {
        workflowEventData.code = this.propertyId;
      }
      this.workflowService.stepIsReadyToBeMarkedCompleted(this.workflowName, this.workflowStepName, workflowEventData);
      this.submitted.emit();
    } else {
      // if the component isn't part of a workflow step, navigate to the landowner/signup-dm/data-confirm
      this.listingContactService.getByPropertyId(this.propertyId).subscribe(
        (result) => {
          this.listingContactService.setActiveContact(result);
          this.submitted.emit(result);

          if (this.proceedRoute){
            this.router.navigate([this.proceedRoute], {
              relativeTo: this.route,
            });
          }          
        },
        (err) => {
          console.error(err);
          
          this.notify.error('A subscription error occured', null);
        }
      );
    }
  }

  @HostListener("keyup") onKeyUp() {
    console.log(`${this._componentName}.onKeyUp()`);
  }
}
